import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { TailSpin } from "react-loader-spinner";
import { BiExport } from "react-icons/bi";

import AdminTransactionsExportModal from "../../components/admin-export/AdminTransactionsExportModal";
import { DataTable } from "../../components/data-table";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { PROD_LINK, STAGING_LINK } from "../../constant";
import { IS_STAGING } from "../../constant/api";
import type { AdminGiftcardBreakdownEntry } from "../../lib/api/adminGiftcardSummaries";
import type { AdminGiftcardTransaction } from "../../lib/api/adminTransactionsData";
import { useAdminGiftcardSummariesQuery } from "../../lib/api/useAdminGiftcardSummariesQuery";
import { useAdminGiftcardTransactionsDataQuery } from "../../lib/api/useAdminGiftcardTransactionsDataQuery";
import {
  banksLookupMap,
  useBanksLookupQuery,
} from "../../lib/api/useBanksLookupQuery";
import { formatAmount } from "../../utils/FormatPerson";
import AdminGiftcardOrderModal from "./AdminGiftcardOrderModal";
import GiftcardRatesAdminPanel from "./GiftcardRatesAdminPanel";
import {
  buildGiftcardTableRows,
  createGiftcardTableColumns,
  type GiftcardTableRow,
} from "./giftcardTransactionsTable";

function titleFromCamelKey(key: string): string {
  const spaced = key.replace(/([a-z])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function breakdownRows(
  record: Record<string, AdminGiftcardBreakdownEntry> | undefined
): { key: string; label: string; entry: AdminGiftcardBreakdownEntry }[] {
  if (!record || typeof record !== "object") return [];
  return Object.entries(record)
    .map(([key, entry]) => ({
      key,
      label: titleFromCamelKey(key),
      entry,
    }))
    .sort((a, b) => b.entry.count - a.entry.count);
}

const GIFT_CARD_TABLE_LIMIT = 25;

const GiftcardsOverview: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const catalogTab = searchParams.get("tab") === "catalog";

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [giftTablePage, setGiftTablePage] = useState(1);
  const [giftTableSearch, setGiftTableSearch] = useState("");
  const [giftModalOpen, setGiftModalOpen] = useState(false);
  const [giftModalTx, setGiftModalTx] =
    useState<AdminGiftcardTransaction | null>(null);
  const [exportOpen, setExportOpen] = useState(false);

  const dateRangeInvalid = Boolean(startDate && endDate) && startDate > endDate;

  const queryDates = useMemo(() => {
    if (startDate && endDate && !dateRangeInvalid) {
      return { startDate, endDate };
    }
    return {};
  }, [startDate, endDate, dateRangeInvalid]);

  const { data, isPending, isError, error, refetch, isFetching } =
    useAdminGiftcardSummariesQuery({
      ...queryDates,
      enabled: !dateRangeInvalid && !catalogTab,
    });

  useEffect(() => {
    setGiftTablePage(1);
  }, [startDate, endDate, giftTableSearch, dateRangeInvalid]);

  const { data: banksLookupData } = useBanksLookupQuery(
    !dateRangeInvalid && !catalogTab
  );
  const banksByCode = useMemo(
    () => banksLookupMap(banksLookupData),
    [banksLookupData]
  );

  const {
    data: giftTxPayload,
    isPending: giftTxPending,
    isError: giftTxError,
    error: giftTxQueryError,
    refetch: refetchGiftTx,
    isFetching: giftTxFetching,
  } = useAdminGiftcardTransactionsDataQuery({
    page: giftTablePage,
    search: giftTableSearch,
    limit: GIFT_CARD_TABLE_LIMIT,
    ...queryDates,
    enabled: !dateRangeInvalid && !catalogTab,
  });

  const giftTransactions = useMemo(
    () => giftTxPayload?.transactions ?? [],
    [giftTxPayload?.transactions]
  );
  const giftPagination = giftTxPayload?.pagination;

  const giftTableRows = useMemo(
    () => buildGiftcardTableRows(giftTransactions),
    [giftTransactions]
  );

  const openGiftModal = useCallback((row: GiftcardTableRow) => {
    setGiftModalTx(row.raw);
    setGiftModalOpen(true);
  }, []);

  const giftColumns = useMemo(
    () => createGiftcardTableColumns(openGiftModal),
    [openGiftModal]
  );

  const giftTableTotalPages = Math.max(1, giftPagination?.totalPages ?? 1);
  const giftTableTotalCount = giftPagination?.totalCount ?? 0;

  const statusEntries = useMemo(() => {
    if (!data?.statusBreakdown) return [];
    return Object.entries(data.statusBreakdown).sort((a, b) => b[1] - a[1]);
  }, [data?.statusBreakdown]);

  const cardTypeRows = useMemo(
    () => breakdownRows(data?.cardTypeBreakdown),
    [data?.cardTypeBreakdown]
  );

  const cardNameRows = useMemo(
    () => breakdownRows(data?.cardNameBreakdown),
    [data?.cardNameBreakdown]
  );

  return (
    <div className="w-full h-screen flex flex-col overflow-hidden">
      <div className="w-full h-[31px] shrink-0 bg-[#1B1B1B] flex justify-center items-center">
        {IS_STAGING ? (
          <h4 className="font-medium text-[#AEBCC6] text-[12px]">
            You are in Staging:{" "}
            <span>
              <a href={PROD_LINK} className="text-[#03C668]">
                Switch to Prod
              </a>
            </span>
          </h4>
        ) : (
          <h4 className="font-medium text-[#AEBCC6] text-[12px]">
            You are in PROD:{" "}
            <span>
              <a href={STAGING_LINK} className="text-[#DF4E4E]">
                Switch to Staging
              </a>
            </span>
          </h4>
        )}
      </div>
      <div className="flex flex-1 min-h-0 w-full flex-col lgss:flex-row overflow-hidden">
        <Sidebar />
        <div className="w-full lgss:w-10/12 flex-1 min-h-0 min-w-0 flex flex-col overflow-y-auto bg-[#13181B]">
          <Navbar />
          <div className="flex-1 px-5 pt-6 pb-24 mds:px-8 lgss:px-10 font-manrope bg-[#13181B] max-w-[1600px] mx-auto w-full">
            <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 flex-1">
                <h1 className="font-sora text-xl font-semibold tracking-tight text-white">
                  Gift cards
                </h1>
                <p className="mt-1 max-w-xl text-[13px] leading-relaxed text-[#869AA9]">
                  {catalogTab
                    ? "Manage which gift cards exist in the app, their thumbnails, and per-country rates. Add new cards from the picker, then configure rates in the table."
                    : "Trade summaries for the selected period. Leave dates empty for all-time totals. Use Catalog & rates to add cards and edit pricing."}
                </p>
                <div
                  className="mt-5 inline-flex rounded-xl border border-[#2C363E] bg-[#161E25] p-1"
                  role="tablist"
                  aria-label="Gift cards sections"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={!catalogTab}
                    onClick={() => {
                      const p = new URLSearchParams(searchParams);
                      p.delete("tab");
                      p.delete("card");
                      setSearchParams(p);
                    }}
                    className={`rounded-lg px-4 py-2 text-[13px] font-semibold transition ${
                      !catalogTab
                        ? "bg-secondary_button text-white shadow-sm"
                        : "text-[#869AA9] hover:text-white"
                    }`}
                  >
                    Activity
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={catalogTab}
                    onClick={() => {
                      const p = new URLSearchParams(searchParams);
                      p.set("tab", "catalog");
                      setSearchParams(p);
                    }}
                    className={`rounded-lg px-4 py-2 text-[13px] font-semibold transition ${
                      catalogTab
                        ? "bg-secondary_button text-white shadow-sm"
                        : "text-[#869AA9] hover:text-white"
                    }`}
                  >
                    Catalog &amp; rates
                  </button>
                </div>
              </div>

              {!catalogTab ? (
                <div
                  className="w-full rounded-xl border border-[#2C363E] bg-[#161E25]/80 p-4 shadow-[0_4px_24px_rgba(0,0,0,0.25)] lg:w-auto lg:min-w-[320px]"
                  role="group"
                  aria-label="Date range filter"
                >
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-[#869AA9]">
                    Date range
                  </p>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <div className="min-w-0 flex-1">
                      <label
                        htmlFor="giftcards-summary-start"
                        className="mb-1 block text-[11px] text-[#5C6B76]"
                      >
                        From
                      </label>
                      <input
                        id="giftcards-summary-start"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#1C2227] px-3 text-[13px] text-[#E7EBEE] shadow-inner outline-none transition focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <label
                        htmlFor="giftcards-summary-end"
                        className="mb-1 block text-[11px] text-[#5C6B76]"
                      >
                        To
                      </label>
                      <input
                        id="giftcards-summary-end"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#1C2227] px-3 text-[13px] text-[#E7EBEE] shadow-inner outline-none transition focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setStartDate("");
                        setEndDate("");
                      }}
                      className="h-10 shrink-0 rounded-lg border border-[#2C363E] px-4 text-[12px] font-semibold text-[#AEBCC6] transition hover:border-[#3A4852] hover:bg-[#273037] hover:text-white active:scale-[0.98]"
                    >
                      Reset
                    </button>
                  </div>
                  {(startDate || endDate) && !(startDate && endDate) && (
                    <p className="mt-2 text-[11px] text-[#5C6B76]">
                      Select both dates to filter; otherwise all-time data is
                      shown.
                    </p>
                  )}
                </div>
              ) : null}
            </header>

            {catalogTab ? <GiftcardRatesAdminPanel /> : null}

            {!catalogTab && dateRangeInvalid && (
              <div
                className="rounded-xl border border-amber-500/35 bg-amber-500/[0.07] px-5 py-8 text-center mb-6"
                role="alert"
              >
                <p className="text-[14px] font-semibold text-amber-200/95">
                  Invalid date range
                </p>
                <p className="text-[13px] text-[#AEBCC6] mt-2 max-w-md mx-auto leading-relaxed">
                  Choose an end date on or after the start date, or reset the
                  filter.
                </p>
              </div>
            )}

            {!catalogTab && !dateRangeInvalid && isPending && (
              <div
                className="flex flex-col items-center justify-center py-24 gap-4 rounded-xl border border-[#2C363E] bg-[#161E25]/40"
                aria-busy="true"
              >
                <TailSpin color="#0B75C2" height={40} width={40} />
                <p className="text-[13px] text-[#869AA9]">Loading summaries…</p>
              </div>
            )}

            {!catalogTab && !dateRangeInvalid && isError && (
              <div className="rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-4 text-[13px] text-red-100/95 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <span>
                  {error?.response?.data &&
                  typeof error.response.data === "object" &&
                  "message" in error.response.data
                    ? String(
                        (error.response.data as { message?: string }).message
                      )
                    : "Could not load gift card summaries."}
                </span>
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="shrink-0 rounded-lg bg-red-500/20 px-4 py-2 text-[12px] font-semibold text-red-200 border border-red-500/40 hover:bg-red-500/30 transition"
                >
                  Retry
                </button>
              </div>
            )}

            {!catalogTab && !dateRangeInvalid && (
              <div className="w-full flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mt-4 mb-3">
                <h4 className="text-white font-sora font-semibold text-[12px] shrink-0">
                  Activity
                </h4>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <button
                    type="button"
                    onClick={() => setExportOpen(true)}
                    className="flex items-center justify-center gap-2 cursor-pointer opacity-90 hover:opacity-100"
                    aria-label="Export summaries and gift card trades"
                  >
                    <BiExport className="text-white text-lg" aria-hidden />
                    <span className="uppercase font-sora font-semibold text-[#869AA9] text-[10px] mt-0.5">
                      Export
                    </span>
                  </button>
                </div>
              </div>
            )}

            {!catalogTab &&
              !dateRangeInvalid &&
              !isPending &&
              !isError &&
              data && (
                <>
                  {isFetching && !isPending && (
                    <div className="mb-4 flex items-center gap-2 text-[12px] text-[#869AA9]">
                      <span
                        className="inline-block h-3 w-3 rounded-full border-2 border-[#869AA9] border-t-transparent animate-spin"
                        aria-hidden
                      />
                      Refreshing…
                    </div>
                  )}

                  <section
                    className="rounded-2xl border border-[#2C363E] bg-gradient-to-b from-[#1C2227] to-[#181D22] p-5 sm:p-7 mb-6 shadow-[0_8px_32px_rgba(0,0,0,0.28)]"
                    aria-labelledby="giftcard-total-heading"
                  >
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="rounded-full border border-[#3A4852] bg-[#0D1114]/80 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#AEBCC6]">
                        USD
                      </span>
                    </div>
                    <p
                      id="giftcard-total-heading"
                      className="text-[11px] font-bold text-[#869AA9] uppercase tracking-widest"
                    >
                      Total trade value
                    </p>
                    <p className="font-sora font-bold text-white text-2xl sm:text-4xl mt-2 tabular-nums">
                      ${formatAmount(data.totalAmountUSD)}
                    </p>
                    <p className="text-[13px] text-[#869AA9] mt-3">
                      <span className="font-semibold text-[#E7EBEE]">
                        {data.totalCount.toLocaleString()}
                      </span>{" "}
                      orders · Avg.{" "}
                      <span className="font-semibold text-[#BEE1FB] tabular-nums">
                        ${formatAmount(data.averageOrderValue)}
                      </span>{" "}
                      per order
                    </p>
                  </section>

                  <section
                    className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6"
                    aria-label="Order counts"
                  >
                    {[
                      {
                        label: "Pending",
                        value: data.totalPending,
                        tone: "amber" as const,
                      },
                      {
                        label: "Completed",
                        value: data.totalCompleted,
                        tone: "emerald" as const,
                      },
                      {
                        label: "Failed",
                        value: data.totalFailed,
                        tone: "red" as const,
                      },
                      {
                        label: "Total",
                        value: data.totalCount,
                        tone: "neutral" as const,
                      },
                    ].map((card) => {
                      const toneCls =
                        card.tone === "amber"
                          ? "border-amber-500/30 bg-amber-500/[0.06] text-amber-200"
                          : card.tone === "emerald"
                          ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-200"
                          : card.tone === "red"
                          ? "border-red-500/30 bg-red-500/[0.06] text-red-200"
                          : "border-[#2C363E] bg-[#161E25]/60 text-white";
                      return (
                        <div
                          key={card.label}
                          className={`rounded-xl border px-4 py-4 ${toneCls}`}
                        >
                          <p className="text-[10px] font-bold uppercase tracking-wider text-[#869AA9]">
                            {card.label}
                          </p>
                          <p className="mt-2 font-sora text-2xl font-bold tabular-nums">
                            {card.value.toLocaleString()}
                          </p>
                        </div>
                      );
                    })}
                  </section>

                  {statusEntries.length > 0 && (
                    <section className="rounded-xl border border-[#2C363E] bg-[#161E25]/50 p-4 sm:p-5 mb-6">
                      <h3 className="text-[12px] font-semibold text-[#E7EBEE] mb-3">
                        Status breakdown
                      </h3>
                      <ul className="flex flex-wrap gap-2">
                        {statusEntries.map(([status, count]) => (
                          <li
                            key={status}
                            className="inline-flex items-center gap-2 rounded-lg border border-[#2C363E] bg-[#0D1114]/60 px-3 py-2"
                          >
                            <span className="text-[11px] font-bold uppercase tracking-wide text-[#AEBCC6]">
                              {status}
                            </span>
                            <span className="text-[14px] font-semibold tabular-nums text-white">
                              {count.toLocaleString()}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    <section className="rounded-xl border border-[#2C363E] bg-[#161E25]/40 overflow-hidden">
                      <div className="border-b border-[#2C363E] px-4 py-3 bg-[#1C2227]/80">
                        <h3 className="text-[13px] font-bold text-white">
                          By card type
                        </h3>
                        <p className="text-[11px] text-[#5C6B76] mt-0.5">
                          Submission / receipt category
                        </p>
                      </div>
                      {cardTypeRows.length === 0 ? (
                        <p className="px-4 py-8 text-center text-[13px] text-[#869AA9]">
                          No card type data for this range.
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[320px] text-left text-[13px]">
                            <thead className="bg-[#0D1114]/80 text-[11px] font-semibold uppercase tracking-wide text-[#869AA9]">
                              <tr>
                                <th className="px-4 py-2.5">Type</th>
                                <th className="px-4 py-2.5 text-right tabular-nums">
                                  Count
                                </th>
                                <th className="px-4 py-2.5 text-right tabular-nums">
                                  Trade value
                                </th>
                              </tr>
                            </thead>
                            <tbody className="text-[#E7EBEE]">
                              {cardTypeRows.map(({ key, label, entry }) => (
                                <tr
                                  key={key}
                                  className="border-t border-[#2C363E] bg-[#13181B]/40"
                                >
                                  <td className="px-4 py-3 font-medium text-white">
                                    {label}
                                  </td>
                                  <td className="px-4 py-3 text-right tabular-nums">
                                    {entry.count.toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3 text-right tabular-nums text-[#BEE1FB]">
                                    ${formatAmount(entry.totalTradeValue)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </section>

                    <section className="rounded-xl border border-[#2C363E] bg-[#161E25]/40 overflow-hidden">
                      <div className="border-b border-[#2C363E] px-4 py-3 bg-[#1C2227]/80">
                        <h3 className="text-[13px] font-bold text-white">
                          By card name
                        </h3>
                        <p className="text-[11px] text-[#5C6B76] mt-0.5">
                          Brand / product
                        </p>
                      </div>
                      {cardNameRows.length === 0 ? (
                        <p className="px-4 py-8 text-center text-[13px] text-[#869AA9]">
                          No card name data for this range.
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[280px] text-left text-[13px]">
                            <thead className="bg-[#0D1114]/80 text-[11px] font-semibold uppercase tracking-wide text-[#869AA9]">
                              <tr>
                                <th className="px-4 py-2.5">Card</th>
                                <th className="px-4 py-2.5 text-right tabular-nums">
                                  Count
                                </th>
                                <th className="px-4 py-2.5 text-right tabular-nums">
                                  Trade value
                                </th>
                              </tr>
                            </thead>
                            <tbody className="text-[#E7EBEE]">
                              {cardNameRows.map(({ key, label, entry }) => (
                                <tr
                                  key={key}
                                  className="border-t border-[#2C363E] bg-[#13181B]/40"
                                >
                                  <td className="px-4 py-3 font-medium text-white">
                                    {label}
                                  </td>
                                  <td className="px-4 py-3 text-right tabular-nums">
                                    {entry.count.toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3 text-right tabular-nums text-[#BEE1FB]">
                                    ${formatAmount(entry.totalTradeValue)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </section>
                  </div>

                  <div id="giftcard-transactions" className="mt-2 mb-4">
                    <div className="mb-3">
                      <h3 className="text-[15px] font-bold text-white font-sora">
                        Gift card trades
                      </h3>
                      <p className="text-[12px] text-[#5C6B76] mt-0.5">
                        From admin transaction data with type giftcard. Pending
                        rows: open review to see proof, then confirm to create
                        the payout and mark the trade completed (legacy gift
                        card flow).
                      </p>
                    </div>
                    <div className="mb-4">
                      <input
                        type="search"
                        value={giftTableSearch}
                        onChange={(e) => setGiftTableSearch(e.target.value)}
                        placeholder="Search transactions"
                        className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#161E25]/80 px-3 text-[13px] text-[#E7EBEE] outline-none transition focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
                      />
                    </div>

                    {giftTxFetching && !giftTxPending && (
                      <p className="text-[12px] text-[#869AA9] mb-2">
                        Updating list…
                      </p>
                    )}

                    {giftTxError && (
                      <div className="rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-3 text-[13px] text-red-100/95 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <span>
                          {axios.isAxiosError(giftTxQueryError) &&
                          giftTxQueryError.response?.data &&
                          typeof giftTxQueryError.response.data === "object" &&
                          "message" in giftTxQueryError.response.data
                            ? String(
                                (
                                  giftTxQueryError.response.data as {
                                    message?: string;
                                  }
                                ).message
                              )
                            : "Could not load gift card trades."}
                        </span>
                        <button
                          type="button"
                          onClick={() => void refetchGiftTx()}
                          className="shrink-0 rounded-lg bg-red-500/20 px-3 py-1.5 text-[12px] font-semibold text-red-200 border border-red-500/40"
                        >
                          Retry
                        </button>
                      </div>
                    )}

                    {giftTxPending && !giftTxError && (
                      <div className="flex justify-center py-16 rounded-xl border border-[#2C363E] bg-[#161E25]/40">
                        <TailSpin color="#0B75C2" height={36} width={36} />
                      </div>
                    )}

                    {!giftTxPending && !giftTxError && (
                      <DataTable<GiftcardTableRow>
                        columns={giftColumns}
                        data={giftTableRows}
                        currentPage={giftTablePage}
                        setCurrentPage={setGiftTablePage}
                        totalPage={giftTableTotalPages}
                        count={giftTableTotalCount}
                        limit={GIFT_CARD_TABLE_LIMIT}
                        emptyContent="No gift card trades for this filter."
                        className="rounded-xl border border-[#2C363E] overflow-hidden"
                        fixedBodyHeight="min(520px, 48vh)"
                      />
                    )}
                  </div>

                  <AdminGiftcardOrderModal
                    open={giftModalOpen}
                    onClose={() => {
                      setGiftModalOpen(false);
                      setGiftModalTx(null);
                    }}
                    transaction={giftModalTx}
                    banksByCode={banksByCode}
                  />
                </>
              )}

            {!catalogTab && (
              <AdminTransactionsExportModal
                open={exportOpen}
                onClose={() => setExportOpen(false)}
                title="Gift cards"
                exportContext={{ type: "giftcard" }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftcardsOverview;
